import { create } from 'zustand';
import { getToken, isApiError, reloadToken, setToken, setUnauthorizedHandler } from '../api/client';
import { partner } from '../api/partner';
import type { Me } from '../api/types';
import { TOKEN_KEY } from '../config';
import { t, useI18n } from '../i18n';
import { toast } from './toast';

/**
 * - anon: no session → login screen
 * - authed: partner company member
 * - not_partner: logged in, but the organization is not a partner company (staff of the sponsor) → refused
 * - must_change: the password was issued by an admin → forced change screen
 */
export type SessionStatus = 'unknown' | 'loading' | 'anon' | 'authed' | 'not_partner' | 'must_change' | 'error';

/** What the login form should do next. */
export type LoginOutcome =
  | { kind: 'ok' }
  | { kind: 'totp' }
  | { kind: 'choose_org'; organizations: string[] }
  | { kind: 'not_partner' }
  | { kind: 'must_change' }
  | { kind: 'error'; code: string; status: number };

interface SessionState {
  status: SessionStatus;
  me: Me | null;
  /** The server ended the session while the app was in use (401): the login screen says so. */
  expired: boolean;
  /** Probe /auth/me with the stored token. Without a token → anon (we never piggyback on the CRM's cookie). */
  load: () => Promise<void>;
  login: (email: string, password: string, totp?: string, organizationId?: string, remember?: boolean) => Promise<LoginOutcome>;
  changePassword: (current: string, next: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Any 401 from the API. */
  expire: () => void;
}

function statusFor(me: Me): SessionStatus {
  if (me.is_partner_company !== true) return 'not_partner';
  if (me.user?.must_change_password) return 'must_change';
  return 'authed';
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'unknown',
  me: null,
  expired: false,

  async load() {
    if (!getToken()) {
      set({ status: 'anon', me: null });
      return;
    }
    set((s) => ({ status: s.status === 'authed' ? 'authed' : 'loading' }));
    try {
      const me = await partner.me();
      const status = statusFor(me);
      if (status === 'not_partner') {
        await dropSession();
        set({ status, me: null });
        return;
      }
      useI18n.getState().setAccountLocale(me.user?.locale);
      set({ status, me });
    } catch (e) {
      if (isApiError(e) && e.status === 401) {
        setToken(null);
        set({ status: 'anon', me: null });
      } else {
        set({ status: 'error' });
      }
    }
  },

  async login(email, password, totp, organizationId, remember = true) {
    try {
      const r = await partner.login({
        email: email.trim(),
        password,
        ...(totp ? { totp: totp.replace(/\s+/g, '') } : {}),
        ...(organizationId ? { organization_id: organizationId } : {}),
      });
      setToken(r.token, remember);
    } catch (e) {
      if (isApiError(e)) {
        if (e.status === 401 && e.code === 'totp_required') return { kind: 'totp' };
        if (e.status === 409 && e.code === 'choose_organization') {
          const ids = typeof e.detail === 'string' ? e.detail.split(',').map((x) => x.trim()).filter(Boolean) : [];
          return { kind: 'choose_org', organizations: ids };
        }
        return { kind: 'error', code: e.code, status: e.status };
      }
      return { kind: 'error', code: 'network_error', status: 0 };
    }
    try {
      const me = await partner.me();
      const status = statusFor(me);
      if (status === 'not_partner') {
        // a staff session must not live in the partner app: close it right away
        await dropSession();
        set({ status, me: null });
        return { kind: 'not_partner' };
      }
      useI18n.getState().setAccountLocale(me.user?.locale);
      set({ status, me, expired: false });
      return status === 'must_change' ? { kind: 'must_change' } : { kind: 'ok' };
    } catch (e) {
      setToken(null);
      return { kind: 'error', code: isApiError(e) ? e.code : 'network_error', status: isApiError(e) ? e.status : 0 };
    }
  },

  async changePassword(current, next) {
    await partner.changePassword(current, next);
    const me = get().me;
    if (me) set({ status: 'authed', me: { ...me, user: { ...me.user, must_change_password: false } } });
  },

  async logout() {
    await dropSession();
    useI18n.getState().setAccountLocale(null);
    set({ status: 'anon', me: null, expired: false });
  },

  expire() {
    const wasIn = get().status === 'authed' || get().status === 'must_change';
    setToken(null);
    useI18n.getState().setAccountLocale(null);
    set({ status: 'anon', me: null, expired: wasIn });
  },
}));

async function dropSession() {
  try {
    if (getToken()) await partner.logout();
  } catch {
    /* already gone */
  }
  setToken(null);
}

setUnauthorizedHandler(() => useSession.getState().expire());

const SWITCH_NOTICE_KEY = 'xq.partner.switched';

/** True when this tab's token is the remembered one (localStorage), i.e. shared with the other tabs of the browser. */
function tokenIsShared(): boolean {
  try {
    return !!getToken() && globalThis.localStorage?.getItem(TOKEN_KEY) === getToken();
  } catch {
    return false;
  }
}

/**
 * Another tab of this browser signed in as someone else or signed out. The remembered token is shared by all tabs of
 * the origin, so without this the tab would quietly become the other user at the next reload (QA phase 3). Now the
 * tab follows at once, starts from the home screen (no screens of the previous user stay open) and says who is
 * signed in. A tab signed in without «remember me» keeps its own token (sessionStorage) and is not affected.
 */
export function startSessionSync(): () => void {
  let sharedAtStart = tokenIsShared();
  const onStorage = (e: StorageEvent) => {
    if (e.key !== TOKEN_KEY && e.key !== null) return;
    let stored: string | null = null;
    try {
      stored = globalThis.localStorage?.getItem(TOKEN_KEY) ?? null;
    } catch {
      return;
    }
    const mine = getToken();
    if (stored === mine) return;
    if (mine && !sharedAtStart) return; // own per-tab session
    const prev = useSession.getState().me?.user?.id ?? null;
    if (!stored) {
      if (mine) {
        useSession.getState().expire();
        toast(t('session.signedOutElsewhere'));
      }
      return;
    }
    reloadToken();
    sharedAtStart = true;
    void useSession
      .getState()
      .load()
      .then(() => {
        const me = useSession.getState().me;
        if (prev && me?.user?.id && me.user.id !== prev) {
          try {
            globalThis.sessionStorage?.setItem(SWITCH_NOTICE_KEY, me.user.full_name || me.user.email || '');
          } catch {
            /* the notice is a convenience */
          }
          window.location.replace('/');
        }
      });
  };
  window.addEventListener('storage', onStorage);
  // the notice left by the reload above
  try {
    const who = globalThis.sessionStorage?.getItem(SWITCH_NOTICE_KEY);
    if (who !== null && who !== undefined) {
      globalThis.sessionStorage?.removeItem(SWITCH_NOTICE_KEY);
      setTimeout(() => toast(t('session.switched', { name: who })), 0);
    }
  } catch {
    /* ignore */
  }
  // a login in this tab makes its token the shared one
  const unsub = useSession.subscribe((s, p) => {
    if (s.me?.user?.id !== p.me?.user?.id) sharedAtStart = tokenIsShared();
  });
  return () => {
    window.removeEventListener('storage', onStorage);
    unsub();
  };
}

/** Office roles (owner/manager/operator) vs driver: drivers see their own trips first. */
export function isDriver(me: Me | null): boolean {
  return me?.role === 'driver';
}

export function canRespond(me: Me | null): boolean {
  return me?.role === 'owner' || me?.role === 'manager';
}

export function canWriteCases(me: Me | null): boolean {
  return me?.role === 'owner' || me?.role === 'manager' || me?.role === 'operator';
}
